import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import UserContext from '../../contexts/UserContext';
import authenticate from '../../utils/Authenticate';
import { isAdminRole } from '../../utils/roles';
import { formatMoney } from '../../utils/currency';

const MasterPoDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [poData, setPoData] = useState(null);
  const [zoneGroups, setZoneGroups] = useState({});
  const [message, setMessage] = useState('');
  const { user, setUser } = useContext(UserContext);
  const[poNumber,setPoNumber] = useState('');

  // Check if PO can be edited (only pending status and admin role)
  const canEditPO = () => {
    console.log(user.role);
    console.log(poData);
    if (!isAdminRole(user?.role)) return false;
    
    // Check if ALL individual POs have "Pending" status
    if (!poData?.pos || poData.pos.length === 0) return false;

     console.log(poData.pos.map(p => p.status));
    
    return poData.pos.every(po => po.status?.toLowerCase() === 'pending');
  };

  // Get overall status based on individual PO statuses
  const getOverallStatus = () => {
    if (!poData?.pos || poData.pos.length === 0) return 'N/A';
    
    const statuses = poData.pos.map(po => po.status);
    const uniqueStatuses = [...new Set(statuses)];
    
    // If all have same status, return that status
    if (uniqueStatuses.length === 1) {
      return uniqueStatuses[0];
    }
    
    // If mixed statuses, return "Mixed"
    return `Mixed (${uniqueStatuses.join(', ')})`;
  };

  useEffect(() => {

    
    if (!user) authenticate(setUser, navigate);
  }, [user, setUser, navigate]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URI}/api/po/getMasterPO?id=${id}`, { withCredentials: true })
      .then((res) => {
        const masterData = res.data.data;
        const grouped = {};
        setPoNumber(masterData.PONumber);
        masterData.pos.forEach((po) => {
          const zoneDesc = po.zone?.desc || "Unassigned Zone";

          if (!grouped[zoneDesc]) {
            grouped[zoneDesc] = {
              zoneDesc,
              supplierName: po.supplier?.name || "N/A",
              supplierDetails: po.supplier,
              items: [],
              deliveryDate: po.deliveryDate,
              poId: po._id,
              status: po.status || "N/A", // PO-level status
              grn: po.grn,
              poIncharge: po.poIncharge,
              remarks: po.remarks
            };
          }

          po.items.forEach((item) => {
            grouped[zoneDesc].items.push({
              ...item,
              itemName: item.item?.name || "Unknown Item",
              poId: po._id,
              deliveryDate: po.deliveryDate,
              status: item.status || "N/A", // Item-level status
            });
          });
        });

        setPoData(masterData);
        setZoneGroups(grouped);
      })
      .catch(() => setMessage('Failed to load PO details'));
  }, [id]);

  if (!poData) return <div className="p-6 text-center">Loading...</div>;

  return (
    <div className="bg-gray-50 min-h-screen">
      <style>{`
        @media print {
          body, .bg-gray-50 { background-color: #fff !important; }
          .print-hide, button { display: none !important; }
          .shadow-md, .shadow-sm { box-shadow: none !important; }
          table, th, td { page-break-inside: avoid; }
        }
        table { width: 100%; border-collapse: collapse; margin-top: 1rem; }
        th, td { padding: 0.5rem; border: 1px solid #ddd; text-align: left; }
        th { background-color: #fffbe6; font-weight: bold; }
        tr:nth-child(even) { background-color: #f4f4f4; }
      `}</style>

      {/* Header */}
      <div className="bg-lime-800 p-4 flex items-center text-white shadow-md">
        <button onClick={() => navigate(-1)} className="text-xl font-bold">←</button>
        <h2 className="text-xl md:text-2xl font-semibold ml-4">Master PO #{poNumber}</h2>
        
        {/* Action Buttons */}
        <div className="flex gap-2 ml-auto">
          {/* Edit Button - Only show for Admin and Pending status */}
          {canEditPO() && (
            <button 
              onClick={() => navigate(`/central/po/edit/${id}`)}
              className="bg-blue-500 hover:bg-blue-700 text-white px-4 py-2 rounded transition-colors"
              title="Edit Purchase Order"
            >
              Edit PO
            </button>
          )}
          
          {/* View PO Button */}
          <Link to="/central/po/print" state={{ poData }}>
            <button className="bg-green-500 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors">
              View PO
            </button>
          </Link>
        </div>
      </div>

      {/* Status Indicator */}
      <div className="max-w-5xl mx-auto px-4 pt-4">
        <div className="flex items-center justify-center mb-4">
          <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
            getOverallStatus() === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
            getOverallStatus() === 'Approved' ? 'bg-green-100 text-green-800' :
            getOverallStatus() === 'Delivered' ? 'bg-blue-100 text-blue-800' :
            getOverallStatus().startsWith('Mixed') ? 'bg-orange-100 text-orange-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            Overall Status: {getOverallStatus()}
          </span>
        </div>
        
        {/* Edit Restriction Message */}
        {isAdminRole(user?.role) && !canEditPO() && !getOverallStatus().includes('pending') && (
          <div className="bg-amber-100 border border-amber-400 text-amber-700 px-4 py-2 rounded mb-4 text-center">
            <span className="text-sm">
              ⚠️ This PO can only be edited when all individual POs have "Pending" status
            </span>
          </div>
        )}
        
        {isAdminRole(user?.role) && getOverallStatus().startsWith('Mixed') && (
          <div className="bg-amber-100 border border-amber-400 text-amber-700 px-4 py-2 rounded mb-4 text-center">
            <span className="text-sm">
              ⚠️ This Master PO has mixed statuses. All individual POs must be "Pending" to edit
            </span>
          </div>
        )}
        
        {!isAdminRole(user?.role) && (
          <div className="bg-gray-100 border border-gray-400 text-gray-700 px-4 py-2 rounded mb-4 text-center">
            <span className="text-sm">
              🔒 Only Admins can edit Purchase Orders
            </span>
          </div>
        )}
      </div>

      {/* Items Grouped by Zone */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center">Items Grouped by Zone</h2>

        {Object.entries(zoneGroups).map(([zoneDesc, group]) => (
          <div key={zoneDesc} className="mb-10 border rounded-lg p-5 bg-white shadow-sm">
            <h3 className="text-lg font-bold text-yellow-700 mb-1">Zone: {zoneDesc}</h3>
            <div className="text-sm text-gray-700 mb-4">
              <p><strong>Supplier:</strong> {group.supplierName}</p>
              <p><strong>Zone PO Status:</strong> 
                <span className={`ml-1 px-2 py-1 rounded text-xs font-semibold ${
                  group.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                  group.status === 'Approved' ? 'bg-green-100 text-green-800' :
                  group.status === 'Delivered' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {group.status}
                </span>
              </p>
              {group.poIncharge && <p><strong>PO Incharge:</strong> {group.poIncharge}</p>}
              {group.grn && <p><strong>GRN: </strong>{group.grn}</p>}
              {group.remarks && <p><strong>Remarks:</strong> {group.remarks}</p>}
            </div>

            {group.items.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="text-center">Quantity</th>
                    <th className="text-center">Unit Price</th>
                    <th className="text-center">Total</th>
                    <th className="text-center">Item Status</th>
                    <th className="text-center">PO ID</th>
                    <th className="text-center">Delivery Date</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.itemName}</td>
                      <td className="text-center">{item.quantity}</td>
                      <td className="text-center">{formatMoney(user, item.price)}</td>
                      <td className="text-center">{formatMoney(user, item.price * item.quantity)}</td>
                      <td className="text-center">{item.status}</td>
                      <td className="text-center">{poNumber}</td>
                      <td className="text-center">{new Date(item.deliveryDate).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="bg-red-100 text-red-600 p-4 rounded text-center">
                No items found for this zone.
              </div>
            )}
          </div>
        ))}

        {/* Error Message */}
        {message && (
          <div className="bg-red-100 text-red-600 p-4 rounded text-center mt-4">
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default MasterPoDetails;